import {
  faBell,
  faClockRotateLeft,
  faComment,
  faEdit,
  faLanguage,
  faPencil,
  faTrash,
  faUserLock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useCaseGetCommentHistory,
  useCaseTranslateComment,
} from 'api/case/case';
import { ContextMenuOption } from 'components/ContextMenu';
import EllipsisContextMenu from 'components/ContextMenu/EllipsisContextMenu';
import Modal from 'components/Modal';
import useModalStack from 'contexts/modal/useModalStack';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { EventSection } from './components';
import DefaultEventHeader from './DefaultEventHeader';
import useMe from 'contexts/authentication/useMe';
import Roles from 'constants/Roles';
import RichMessage from 'components/RichMessage';
import useGlobalSettings from 'hooks/useGlobalSettings';
import { CaseEventType, EventModel, GlobalSettingEnum } from 'api/model';
import { emptyGuid } from 'constants/AppConstants';

const parentHoverButton = 'parentHoverButton';
const MyEventSection = styled(EventSection)`
  &:hover .${parentHoverButton}, ${`.${parentHoverButton}`}:focus-within {
    background: ${({ theme }) => theme.colors.background.button};
    color: ${({ theme }) => theme.colors.foreground.button};
  }
`;

const Horiz = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-top: 5px;

  & > *:last-child {
    align-self: flex-end;
  }
`;

const NotifyIcon = styled(FontAwesomeIcon)`
  margin-left: 10px;
  color: ${({ theme }) => theme.colors.foreground.newMessage};
`;

const EditedIcon = styled(FontAwesomeIcon)`
  margin-left: 6px;
  font-size: 0.8em;
  opacity: 0.7;
`;

const HistorySection = styled.div`
  max-height: 350px;
  overflow-y: auto;
  margin-top: 10px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 4px;
`;

const HistoryItem = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  &:last-child {
    border-bottom: none;
  }
`;

const HistoryMeta = styled.div`
  font-size: 0.85em;
  color: ${({ theme }) => theme.colors.foreground.secondary};
  margin-bottom: 4px;
`;

const HistoryMessage = styled.div`
  white-space: pre-wrap;
`;

const TranslationToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.foreground.tint};
  cursor: pointer;
  font-size: 0.85em;
  padding: 4px 0;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const MessageContainer = styled.div`
  flex: 1;
`;

interface Props {
  event: EventModel;
  onEditClick?(): void;
  onDeleteClick?(): void;
}

const Comment: FC<Props> = ({ event, onEditClick, onDeleteClick }) => {
    const modalStack = useModalStack();
    const me = useMe();
    const { parsedData: globalSettings } = useGlobalSettings();
    const hasTranslationAccess =
      globalSettings[GlobalSettingEnum.UsersWithAccessToTranslation]?.[
        me?.userID ?? -1
      ] === true;
    const [showHistory, setShowHistory] = useState(false);
    const [translatedText, setTranslatedText] = useState<string | null>(null);
    const [showTranslated, setShowTranslated] = useState(false);

    const translateMutation = useCaseTranslateComment();

    const { data: history = [], isLoading: isLoadingHistory } = useCaseGetCommentHistory(
        { caseEventId: event.id },
        { query: { enabled: showHistory } }
    );

    const isEdited = event.isEdited ?? false;

    // Load cached translation from event data on mount
    useEffect(() => {
      const cachedTranslation = event.translations?.Swedish;
      if (cachedTranslation) {
        setTranslatedText(cachedTranslation);
      }
    }, [event]);

    const handleTranslateClick = useCallback(async () => {
      if (translatedText) {
        // Translation already exists, just toggle display
        setShowTranslated(true);
        return;
      }

      try {
        const result = await translateMutation.mutateAsync({
          params: {
            eventId: event.id,
            targetLanguage: 'Swedish',
          },
        });
        setTranslatedText(result.translatedText);
        setShowTranslated(true);
      } catch {
        // translation failed silently — user can retry
      }
    }, [event.id, translatedText, translateMutation]);

  const handleHistoryClick = useCallback(() => {
    setShowHistory((prev) => !prev);
  }, []);

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const menuOptions = useMemo((): ContextMenuOption[] => {
    const opts: ContextMenuOption[] = [];

    // Only show translate option if no translation exists yet and user has access
    if (hasTranslationAccess && !translatedText && event.message) {
      opts.push({
        icon: <FontAwesomeIcon icon={faLanguage} />,
        label: translateMutation.isLoading ? 'Översätter...' : 'Översätt',
        onClick: handleTranslateClick,
      });
    }

    if (onEditClick)
      opts.push({
        icon: <FontAwesomeIcon icon={faEdit} />,
        label: 'Redigera',
        onClick: onEditClick,
      });

    if (isEdited && me?.roles.includes(Roles.Admin))
      opts.push({
        icon: <FontAwesomeIcon icon={faClockRotateLeft} />,
        label: 'Historik',
        onClick: handleHistoryClick,
      });

    if (onDeleteClick && me?.roles.includes(Roles.Admin))
      opts.push({
        icon: <FontAwesomeIcon icon={faTrash} />,
        label: 'Ta bort',
        onClick: () =>
          modalStack.push(
            <Modal
              buttons={[
                {
                  icon: <FontAwesomeIcon icon={faTrash} />,
                  label: 'Ta bort',
                  onClick: () => {
                    modalStack.pop();
                    onDeleteClick();
                  },
                },
                { label: 'Avbryt', onClick: () => modalStack.pop() },
              ]}
              onClose={() => modalStack.pop()}
              title="Ta bort kommentar?"
            >
              Är du säker på att du vill ta bort kommentaren?
            </Modal>
          ),
      });

    return opts;
  }, [modalStack, onDeleteClick, onEditClick, isEdited, handleHistoryClick, me, hasTranslationAccess, translatedText, event.message, translateMutation.isLoading, handleTranslateClick]);

  return (
    <MyEventSection>
      <DefaultEventHeader
        created={event.created}
        createdByID={event.createdByID}
        headerText={
          <>
            {event.caseEventType === CaseEventType.Comment ? (
              <>
                <FontAwesomeIcon icon={faComment} /> Kommentar
                {isEdited && <EditedIcon icon={faPencil} title="Redigerad" />}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUserLock} /> Intern kommentar
                {isEdited && <EditedIcon icon={faPencil} title="Redigerad" />}
              </>
            )}
            {!event.readByAdmin && <NotifyIcon icon={faBell} title="Oläst" />}
          </>
        }
        modified={event.modified}
        modifiedByID={event.modifiedByID}
        userName={(event as EventModel & { editedBy?: string }).editedBy || event.createdBy}
        assignmentId={event.assignmentID}
        attachmentFileGuid={
          event.attachmentFileGuid && event.attachmentFileGuid !== emptyGuid
            ? event.attachmentFileGuid
            : undefined
        }
        attachmentFileEnding={event.attachmentFileEnding}
      />

      <Horiz>
        <MessageContainer>
          <RichMessage text={hasTranslationAccess && showTranslated && translatedText ? translatedText : event.message} />
          {hasTranslationAccess && translatedText && (
            <TranslationToggle onClick={() => setShowTranslated(!showTranslated)}>
              <FontAwesomeIcon icon={faLanguage} />
              {showTranslated ? 'Visa original' : 'Visa översättning'}
            </TranslationToggle>
          )}
        </MessageContainer>
        {me?.roles.includes(Roles.Admin) && (
          <EllipsisContextMenu
            className={parentHoverButton}
            options={menuOptions}
            position="right"
          />
        )}
      </Horiz>

      {showHistory && (
        <HistorySection>
          {isLoadingHistory ? (
            <HistoryMeta>Hämtar historik...</HistoryMeta>
          ) : history.length === 0 ? (
            <HistoryMeta>Ingen historik tillgänglig</HistoryMeta>
          ) : (
            history.map((h) => (
              <HistoryItem key={h.id}>
                <HistoryMeta>
                  {formatDate(h.created)} - {h.createdByName}
                </HistoryMeta>
                <HistoryMessage>{h.message}</HistoryMessage>
              </HistoryItem>
            ))
          )}
        </HistorySection>
      )}
    </MyEventSection>
  );
};

export default Comment;
